// ----------------------------- Utilities ------------------------------------

import { useEffect, useRef, useState } from "react";

// Safe JSON stringify with stable indentation
export function safeStringify(obj: unknown): string {
  try {
    return JSON.stringify(obj, null, 2);
  } catch {
    return String(obj);
  }
}

// Flatten JSON into single-level key/value pairs using dot-notation and [index] for arrays
export function flattenJSON(
  input: unknown,
  options?: {
    delimiter?: string;
    arrayStyle?: 'bracket' | 'dotBracket' | 'dot';
    maxDepth?: number;
  }
): Record<string, unknown> {
  const delimiter = options?.delimiter ?? '.';
  const arrayStyle = options?.arrayStyle ?? 'bracket'; // bracket => users[0].name ; dot => users.0.name
  const maxDepth = options?.maxDepth ?? 128;

  const out: Record<string, unknown> = {};
  const seen = new WeakSet<object>();

  function keyJoin(base: string, next: string): string {
    if (!base) return next;
    return `${base}${delimiter}${next}`;
  }

  function push(path: string, value: unknown): void {
    out[path] = value;
  }

  function walk(node: unknown, path = '', d = 0): void {
    if (d > maxDepth) {
      push(path || '__root__', '__MAX_DEPTH_REACHED__');
      return;
    }

    if (node === null || typeof node !== 'object') {
      push(path || '__root__', node);
      return;
    }

    if (seen.has(node as object)) {
      push(path || '__root__', '__CIRCULAR__');
      return;
    }
    seen.add(node as object);

    if (Array.isArray(node)) {
      node.forEach((v, i) => {
        switch (arrayStyle) {
          case 'dotBracket':
            walk(v, `${path ? path + delimiter : ''}[${i}]`, d + 1);
            break;
          case 'bracket':
            walk(v, `${path ? path : ''}[${i}]`, d + 1);
            break;
          case 'dot':
            walk(v, keyJoin(path, String(i)), d + 1);
            break;
        }
      });
      return;
    }

    // Regular object
    for (const [k, v] of Object.entries(node as Record<string, unknown>)) {
      const next = path ? keyJoin(path, k) : k;
      walk(v, next, d + 1);
    }
  }

  walk(input);
  return out;
}

export type IUseClipboardReturn = {
  copied: boolean,
  copy: () => Promise<void>,
};

export function useClipboard(text: string, delay = 1200): IUseClipboardReturn {
  const [copied, setCopied] = useState(false);
  const to = useRef<number | null>(null);
  useEffect(() => {
    to.current && window.clearTimeout(to.current);
  }, []);
  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      to.current = window.setTimeout(() => setCopied(false), delay);
    } catch (e: unknown) {
      setCopied(false);
      to.current = null;
      if (e instanceof Error) {
        console.error(e.message);
      } else {
        console.log(String(e));
      }
    }
  };
  return { copied, copy } as const;
}
